import { useEffect, useState } from "react";
import { LifeBuoy, Loader2, Send, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useWorkspace } from "../../store/WorkspaceContext";

export const STATUS_STYLE = {
  open: "border-[#4c9ffe]/50 bg-[#4c9ffe]/10 text-[#8fc1ff]",
  pending: "border-[#f5a524]/50 bg-[#f5a524]/10 text-[#f5c542]",
  closed: "border-[#3ecf8e]/50 bg-[#3ecf8e]/10 text-[#3ecf8e]",
};
export const StatusPill = ({ status, id }) => <span data-testid={id} className={`flex-none rounded-full border px-2 py-px text-[10px] font-semibold capitalize ${STATUS_STYLE[status] || STATUS_STYLE.open}`}>{status}</span>;

export const Thread = ({ t, testId }) => (
  <div className="space-y-1.5" data-testid={testId}>
    {t.messages.map((m) => (
      <div key={m.id} className={`rounded-lg px-3 py-2 text-[12px] ${m.from === "owner" ? "ml-6 border border-[#4c9ffe]/30 bg-[#4c9ffe]/10" : "mr-6 border border-white/10 bg-black/25"}`} data-testid={`ticket-msg-${m.id}`}>
        <div className="mb-0.5 flex items-center justify-between text-[10px] text-[#6b7079]"><span className="font-semibold text-[#9aa0a8]">{m.from === "owner" ? "Xpattern support" : "You"}</span><span>{new Date(m.at).toLocaleString()}</span></div>
        <p className="whitespace-pre-wrap text-[#e8e9ec]">{m.text}</p>
      </div>
    ))}
  </div>
);

const TicketRow = ({ t, cats, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = () => { setOpen((o) => !o); if (!open && t.customer_unread) api.seenTicket(t.id).then(onChanged).catch(() => {}); };
  const send = async () => {
    if (reply.trim().length < 1) return;
    setBusy(true);
    try { await api.replyTicket(t.id, { message: reply.trim() }); setReply(""); toast.success(t.status === "closed" ? "Ticket reopened and your reply was sent" : "Reply sent"); onChanged(); }
    catch (e) { toast.error(apiError(e, "Could not send reply")); }
    setBusy(false);
  };
  return (
    <div className="border-b border-white/10 last:border-b-0" data-testid={`my-ticket-${t.id}`}>
      <button onClick={toggle} className="flex w-full items-center gap-3 px-3 py-2 text-left text-[12px] hover:bg-white/5" data-testid={`my-ticket-toggle-${t.id}`}>
        {t.customer_unread && <span className="h-2 w-2 flex-none rounded-full bg-[#e5484d] shadow-[0_0_8px_rgba(229,72,77,.8)]" data-testid={`my-ticket-unread-${t.id}`} />}
        <span className="min-w-0 flex-1 truncate"><span className="font-medium">{t.subject}</span><span className="ml-2 text-[#6b7079]">{cats[t.category] || t.category}</span></span>
        <span className="text-[10px] text-[#6b7079]">{new Date(t.updated_at).toLocaleDateString()}</span>
        <StatusPill status={t.status} id={`my-ticket-status-${t.id}`} />
        {open ? <ChevronUp className="h-3.5 w-3.5 text-[#6b7079]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#6b7079]" />}
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3">
          <Thread t={t} testId={`my-ticket-thread-${t.id}`} />
          <div className="flex gap-2">
            <textarea data-testid={`my-ticket-reply-${t.id}`} value={reply} onChange={(e) => setReply(e.target.value)} rows={2} maxLength={4000} placeholder={t.status === "closed" ? "Not fixed? Write here and the ticket reopens…" : "Reply…"} className="field min-h-[44px] flex-1 text-[12px]" />
            <button data-testid={`my-ticket-send-${t.id}`} className="btn-red self-end" disabled={busy || !reply.trim()} onClick={send}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Customer dashboard: open a support ticket and follow the owner's replies
export const TicketsSection = ({ Section, open }) => {
  const { state } = useWorkspace();
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const load = () => api.myTickets().then(setMine).catch(() => {});
  useEffect(() => { if (open) { load(); api.ticketCategories().then(setCats).catch(() => {}); } }, [open]);
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.label]));
  const submit = async () => {
    if (!category) return toast.error("Pick what the issue is about");
    if (message.trim().length < 3) return toast.error("Describe the issue in a few words");
    setBusy(true);
    const b = state.bridge;
    const context = { machine: state.machine?.name, transport: state.connection?.transport, connector: b?.ok ? { host: b.hostname, version: b.version, os: b.os, suggested: b.suggested } : null, app: navigator.userAgent, page: window.location.pathname };
    try { await api.createTicket({ category, subject: subject.trim(), message: message.trim(), context }); toast.success("Ticket sent — we'll reply here and by email."); setCategory(""); setSubject(""); setMessage(""); load(); }
    catch (e) { toast.error(apiError(e, "Could not send the ticket")); }
    setBusy(false);
  };
  return (
    <Section icon={LifeBuoy} title="Support tickets" testId="profile-tickets" className="mt-4">
      <p className="text-[11px] text-[#9aa0a8]">Something not working? Tell us what and we'll get back to you here and by email. Your plotter and connector details are attached automatically.</p>
      <div className="grid gap-2 sm:grid-cols-[220px_1fr]">
        <select data-testid="ticket-category" value={category} onChange={(e) => setCategory(e.target.value)} className="field text-[12px]">
          <option value="">What is it about?</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input data-testid="ticket-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={140} placeholder="Short title (optional)" className="field text-[12px]" />
      </div>
      <textarea data-testid="ticket-message" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000} rows={3} placeholder="What happened, what you expected, and which vehicle / pattern if it applies…" className="field min-h-[72px] text-[12px]" />
      <div className="flex justify-end"><button data-testid="ticket-submit-btn" className="btn-red" disabled={busy} onClick={submit}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit ticket</button></div>
      {mine.length > 0 && (
        <div className="scroll-visible max-h-72 overflow-y-auto rounded-lg border border-white/10 bg-black/25" data-testid="my-tickets">
          {mine.map((t) => <TicketRow key={t.id} t={t} cats={catMap} onChanged={load} />)}
        </div>
      )}
    </Section>
  );
};
